import { isCompletedProjectStatus, normalizeProjectStatus } from "./projectViews";

/** Project fully subscribed (matches Shares Management funded cards). */
export function isFullySoldProject(project) {
  const total = Number(project?.total_shares || 0);
  const sold = Number(project?.shares_sold || 0);
  return total > 0 && sold >= total;
}

function isSecondaryMarketProjectStatus(status) {
  const s = normalizeProjectStatus(status);
  return s === "LOCK_IN" || s === "MATURED";
}

/** Same rules as transfer tab on a funded project card (server also validates on submit). */
export function isInvestorTransferEligible(holding, project) {
  const p = project || holding?.project;
  if (!p || !holding) return false;
  if (isCompletedProjectStatus(p.status)) return false;
  return (
    Number(holding.shares_held || 0) > 0 &&
    isFullySoldProject(p) &&
    isSecondaryMarketProjectStatus(p.status)
  );
}

/** Same rules as redeem tab on a funded project card (server also validates on submit). */
export function isInvestorRedeemEligible(holding, project) {
  const p = project || holding?.project;
  if (!p || !holding) return false;
  if (isCompletedProjectStatus(p.status)) return false;
  return (
    Number(holding.shares_held || 0) > 0 &&
    isFullySoldProject(p) &&
    isSecondaryMarketProjectStatus(p.status) &&
    p.eligible_for_redemption === true
  );
}

export function maxSharesPerRequest(preview, available) {
  const avail = Number(available || 0);
  if (avail <= 0) return 0;
  const cap = Number(preview?.max_shares_per_request);
  if (Number.isFinite(cap) && cap > 0) return Math.min(avail, cap);
  return avail;
}

const shareFmt = new Intl.NumberFormat("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const formatShareQty = (n) => shareFmt.format(Number(n || 0));

/**
 * Client-side checks aligned with POST /investments/redeem/ (max_redemption_pct_of_holdings).
 * Returns investor-facing messages before submit.
 */
export function validateRedemptionSharesInput(shares, available, preview) {
  const sh = Number(shares);
  if (!Number.isFinite(sh) || sh <= 0) {
    return { valid: false, message: "Enter a share amount greater than zero.", requestCap: 0 };
  }
  const avail = Number(available || 0);
  const pending = Number(preview?.shares_pending_redemption || 0);
  if (sh > avail + 0.0001) {
    const pendingNote = pending > 0 ? ` (${formatShareQty(pending)} locked in pending requests)` : "";
    return {
      valid: false,
      message: `Insufficient redeemable shares. Available: ${formatShareQty(avail)}${pendingNote}.`,
      requestCap: maxSharesPerRequest(preview, avail),
    };
  }
  const cap = maxSharesPerRequest(preview, avail);
  if (sh > cap + 0.0001) {
    const pct = preview?.max_redemption_pct_of_holdings;
    const pctNote =
      pct != null && pct !== ""
        ? `maximum ${pct}% of your holding per request (platform setting)`
        : "maximum per request (platform setting)";
    return {
      valid: false,
      message: `This request exceeds the allowed amount. You may redeem up to ${formatShareQty(cap)} shares per request (${pctNote}).`,
      requestCap: cap,
    };
  }
  return { valid: true, message: "", requestCap: cap };
}

export function availableSharesFromPreview(preview) {
  const fromPreview = Number(preview?.shares_available_for_redemption);
  if (Number.isFinite(fromPreview) && fromPreview >= 0) return fromPreview;
  const held = Number(preview?.shares_held || 0);
  const pending = Number(preview?.shares_pending_redemption || 0);
  return Math.max(0, held - pending);
}

export function availableSharesFromHolding(holding) {
  if (!holding) return 0;
  const direct = Number(holding.shares_available_for_redemption);
  if (Number.isFinite(direct) && direct >= 0) return direct;
  return Math.max(0, Number(holding.shares_held || 0) - Number(holding.shares_pending_redemption || 0));
}
